"use client";
import { motion, useReducedMotion } from 'framer-motion';
import { Star, BriefcaseBusiness, Quote } from 'lucide-react';
import { testimonials } from '@/lib/homePage';

// Separate into a memoized component to prevent unnecessary re-renders during animation
const TestimonialCard = ({ testimonial }) => {
  const initials = testimonial.position
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("");

  return (
    <article
      className="group relative w-full p-8 rounded-3xl bg-white border border-gray-100 mb-8 flex flex-col shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.1)] transition-all duration-500"
    >
      {/* Decorative Quote Icon - Hidden from SR */}
      <div className="absolute top-6 right-8 text-gray-50 group-hover:text-blue-50 transition-colors duration-500" aria-hidden="true">
        <Quote size={48} fill="currentColor" />
      </div>

      {/* 1. Header: Client Identity */}
      <div className="relative z-10 flex items-center gap-4 mb-6">
        <div 
          className="flex-shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br from-gray-900 to-gray-700 flex items-center justify-center text-white text-sm font-bold tracking-wider shadow-lg"
          aria-hidden="true"
        >
          {initials}
        </div>
        <div>
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-tight">
            {testimonial.position}
          </h3>
          <p className="text-xs font-medium text-blue-600/80 uppercase">
            {testimonial.company}
          </p>
        </div>
      </div>

      {/* 2. Service Provided Badge */}
      <div className="mb-4">
        <span className="inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest bg-blue-50 text-blue-700 border border-blue-100">
          <span className="sr-only">Service provided: </span>
          {testimonial.service}
        </span>
      </div>

      {/* 3. Rating */}
      <div className="flex gap-1 mb-4" role="img" aria-label={`Rated ${testimonial.rating} out of 5 stars`}>
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            size={16}
            className={`${
              i < Math.floor(testimonial.rating)
                ? "text-green-600 fill-green-600"
                : "text-gray-200"
            }`}
            aria-hidden="true"
          />
        ))}
      </div>

      {/* 4. The Content */}
      <blockquote className="relative z-10">
        <p className="text-gray-600 text-[15px] leading-relaxed font-medium italic">
          &ldquo;{testimonial.review}&rdquo;
        </p>
      </blockquote>

      {/* Bottom accent line */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-1 bg-blue-600 transition-all duration-500 group-hover:w-1/2 rounded-t-full" aria-hidden="true" />
    </article>
  );
};

const Testimonials = () => {
    // Respect system preference for reduced motion
    const shouldReduceMotion = useReducedMotion();
    
    // Split testimonials for two columns
    const firstCol = [...testimonials, ...testimonials];
    const secondCol = [...testimonials, ...testimonials];

    // Animation settings
    const scrollAnimation = {
      animate: shouldReduceMotion ? { y: 0 } : { y: ["0%", "-50%"] },
      reverseAnimate: shouldReduceMotion ? { y: 0 } : { y: ["-50%", "0%"] },
      transition: { duration: 35, ease: "linear", repeat: Infinity }
    };

    return (
        <section 
          id="testimonials"
          className="w-full py-20 bg-gray-50/50 overflow-hidden"
          aria-labelledby="testimonials-heading"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-center">

                    {/* LEFT CONTENT: Sticky Header */}
                    <header className="font-main lg:sticky lg:top-20 space-y-6 self-start">
                        <span className="inline-flex items-center gap-2 border-2 border-brand text-brand px-4 py-2 rounded-md text-sm font-semibold shadow-sm w-fit uppercase tracking-wider">
                            <BriefcaseBusiness className="w-5 h-5" aria-hidden="true" />
                            Testimonials
                        </span>
                        <h2 id="testimonials-heading" className="text-4xl lg:text-6xl font-light text-gray-900 leading-tight">
                            What Our <span className="text-brand font-semibold italic">Clients</span> Say
                        </h2>
                        <p className="text-lg text-gray-600 max-w-md">
                            We’ve helped businesses of all sizes unlock growth and achieve lasting results.
                        </p>
                    </header>

                    {/* RIGHT CONTENT: Dual Vertical Carousels */}
                    <div 
                      className="col-span-2 relative h-[600px] grid grid-cols-1 md:grid-cols-2 gap-6 overflow-hidden"
                      aria-live="off" // Prevents SR from announcing every movement
                    >
                        {/* Column 1 */}
                        <motion.div
                            className="flex flex-col"
                            animate={scrollAnimation.animate}
                            transition={scrollAnimation.transition}
                        >
                            {firstCol.map((t, i) => <TestimonialCard key={`col1-${i}-${t.id}`} testimonial={t} />)}
                        </motion.div>

                        {/* Column 2 */}
                        <motion.div
                            className="flex flex-col hidden md:flex"
                            animate={scrollAnimation.reverseAnimate}
                            transition={scrollAnimation.transition}
                        >
                            {secondCol.map((t, i) => <TestimonialCard key={`col2-${i}-${t.id}`} testimonial={t} />)}
                        </motion.div>

                        {/* Gradient Overlays */}
                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-gray-50 via-transparent to-gray-50 z-10" aria-hidden="true" />
                    </div>

                </div>
            </div>
        </section>
    );
};

export default Testimonials;